
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";

import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Leaf,
  LockKeyhole,
  ShoppingBag,
  Utensils,
  Sparkles,
} from "lucide-react";

import { fetchPriceSummary } from "../../actions/priceActions";
import { useCart } from "../../components/CartContext";

import "./css/CheckoutScreen.css";

/* IMAGE URL HELPER - SAME AS THEME 9 */
const getImageUrl = (image) => {
  if (!image) return "";

  const imageValue =
    typeof image === "string"
      ? image
      : image.url || image.image || "";

  if (!imageValue) return "";

  if (
    imageValue.startsWith("http://") ||
    imageValue.startsWith("https://")
  ) {
    return imageValue;
  }

  return `${
    imageValue.startsWith("/") ? "" : "/"
  }${imageValue}`;
};

const formatPrice = (value) =>
  Number(value || 0).toFixed(2);

function CheckoutScreen() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { restaurantId, tableId } = useParams();

  const { cart, cartCount, cartTotal } = useCart();

  const [priceSummary, setPriceSummary] = useState({
    subtotal: 0,
    tax: 0,
    serviceCharge: 0,
    total: 0,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [priceReady, setPriceReady] = useState(false);

  const homePath =
    `/${restaurantId}/table/${tableId}`;

  /* FETCH BACKEND PRICE SUMMARY */
  useEffect(() => {
    let cancelled = false;

    const calculatePrice = async () => {
      if (!restaurantId || !cart || cart.length === 0) {
        setPriceReady(false);
        return;
      }

      try {
        setLoading(true);
        setError("");
        setPriceReady(false);

        const items = cart.map((item) => ({
          menu_item_id: item.id,
          quantity: item.quantity,
        }));

        const response = await dispatch(
          fetchPriceSummary(restaurantId, items)
        );

        if (cancelled) return;

        if (
          !response ||
          response.subtotal == null ||
          response.total == null
        ) {
          throw new Error(
            "The restaurant did not return a valid price summary."
          );
        }

        const summary = {
          subtotal: Number(response.subtotal),
          tax: Number(response.tax ?? 0),
          serviceCharge: Number(
            response.service_charge ?? 0
          ),
          total: Number(response.total),
        };

        if (
          Object.values(summary).some(
            (value) => !Number.isFinite(value)
          )
        ) {
          throw new Error(
            "The restaurant returned an invalid order total."
          );
        }

        setPriceSummary(summary);
        setPriceReady(true);
      } catch (err) {
        if (cancelled) return;

        console.error("Price summary error:", err);

        setError(
          err?.response?.data?.detail ||
            err?.message ||
            "Unable to calculate your order total."
        );
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    calculatePrice();

    return () => {
      cancelled = true;
    };
  }, [dispatch, restaurantId, cart]);

  /* NAVIGATION */
  const handleBackToCart = () => {
    navigate(`${homePath}/cart`);
  };

  const handleBackToMenu = () => {
    navigate(`${homePath}#theme10-menu`);
  };

  const handleContinue = () => {
    if (!cart || cart.length === 0) {
      handleBackToMenu();
      return;
    }

    if (loading || error || !priceReady) return;

    navigate(`${homePath}/order`, {
      state: {
        subtotal: Number(priceSummary.subtotal),
        tax: Number(priceSummary.tax),
        serviceCharge: Number(priceSummary.serviceCharge),
        total: Number(priceSummary.total),
      },
    });
  };

  /* EMPTY CART */
  if (!cart || cart.length === 0) {
    return (
      <main className="theme10-checkout-page">
        <section className="theme10-checkout-empty">
          <div className="theme10-checkout-empty-art">
            <div className="theme10-checkout-empty-icon">
              <ShoppingBag size={40} strokeWidth={1.5} />
            </div>

            <Sparkles
              className="theme10-checkout-empty-spark"
              size={26}
            />
          </div>

          <span className="theme10-checkout-label">
            YOUR FOOD JOURNEY
          </span>

          <h1>
            Your basket is <em>empty.</em>
          </h1>

          <p>
            A delicious meal begins with a great choice.
            Explore our menu and add your favourite dishes
            before checking out.
          </p>

          <button
            type="button"
            className="theme10-checkout-primary-button"
            onClick={handleBackToMenu}
          >
            Explore Our Menu
            <ArrowRight size={18} />
          </button>

          <div className="theme10-checkout-empty-bottom">
            <Leaf size={15} />
            Fresh flavours await
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="theme10-checkout-page">
      {/* HEADER */}
      <section className="theme10-checkout-header">
        <div className="theme10-checkout-container">
          <button
            type="button"
            className="theme10-checkout-back"
            onClick={handleBackToCart}
          >
            <ArrowLeft size={17} />
            Back to Cart
          </button>

          <div className="theme10-checkout-title-row">
            <div className="theme10-checkout-title-content">
              <span className="theme10-checkout-label">
                ONE STEP CLOSER TO DELICIOUS
              </span>

              <h1>
                Check<em>out.</em>
              </h1>

              <p>
                Take one last look at your favourites
                before continuing to place your order.
              </p>
            </div>

            <div className="theme10-checkout-table">
              <span>YOUR TABLE</span>
              <strong>{tableId}</strong>
            </div>
          </div>
        </div>

        <div className="theme10-checkout-header-decoration" />
      </section>

      {/* MAIN CONTENT */}
      <section className="theme10-checkout-content">
        <div className="theme10-checkout-container">
          <div className="theme10-checkout-layout">
            {/* LEFT SIDE */}
            <div className="theme10-checkout-order">
              <div className="theme10-checkout-section-head">
                <div className="theme10-checkout-section-title">
                  <span className="theme10-checkout-step">
                    01
                  </span>

                  <div>
                    <span className="theme10-checkout-small-label">
                      YOUR SELECTION
                    </span>

                    <h2>Review your order</h2>
                  </div>
                </div>

                <span className="theme10-checkout-item-count">
                  {cartCount}{" "}
                  {cartCount === 1 ? "item" : "items"}
                </span>
              </div>

              {/* ORDER ITEMS */}
              <div className="theme10-checkout-items">
                {cart.map((item) => {
                  const image = getImageUrl(item.image);

                  return (
                    <article
                      className="theme10-checkout-item"
                      key={item.id}
                    >
                      <div className="theme10-checkout-item-image">
                        {image ? (
                          <img
                            src={image}
                            alt={item.name}
                            loading="lazy"
                          />
                        ) : (
                          <div className="theme10-checkout-placeholder">
                            <Utensils size={24} />
                          </div>
                        )}
                      </div>

                      <div className="theme10-checkout-item-info">
                        <span className="theme10-checkout-category">
                          <Leaf size={12} />
                          {item.categoryName || "OUR KITCHEN"}
                        </span>

                        <h3>{item.name}</h3>

                        <p>
                          ₹{formatPrice(item.price)}
                          <small> / item</small>
                        </p>
                      </div>

                      <div className="theme10-checkout-item-quantity">
                        <span>QTY</span>
                        <strong>{item.quantity}</strong>
                      </div>

                      <div className="theme10-checkout-item-total">
                        <span>ITEM TOTAL</span>

                        <strong>
                          ₹{formatPrice(
                            Number(item.price) *
                              Number(item.quantity)
                          )}
                        </strong>
                      </div>
                    </article>
                  );
                })}
              </div>

              {/* TABLE CARD */}
              <div className="theme10-table-card">
                <div className="theme10-table-icon">
                  <span>TABLE</span>
                  <strong>{tableId}</strong>
                </div>

                <div className="theme10-table-info">
                  <span>YOUR DINING SPOT</span>
                  <h3>Table {tableId}</h3>
                  <p>
                    Your order will be prepared and
                    served at this table.
                  </p>
                </div>

                <div className="theme10-table-confirm">
                  <CheckCircle2 size={22} />
                </div>
              </div>

              {/* INFORMATION */}
              <div className="theme10-checkout-info">
                <span className="theme10-checkout-info-icon">
                  <Leaf size={18} />
                </span>

                <p>
                  Please review your items and the final
                  order total before continuing. You can
                  return to your cart to make changes.
                </p>
              </div>
            </div>

            {/* RIGHT SUMMARY */}
            <aside className="theme10-checkout-summary">
              <div className="theme10-summary-top">
                <span className="theme10-summary-number">
                  02
                </span>

                <span className="theme10-summary-label">
                  ORDER TOTAL
                </span>
              </div>

              <h2>
                Your <em>Order.</em>
              </h2>

              <p className="theme10-summary-subtitle">
                A delicious meal for table {tableId}.
              </p>

              <div className="theme10-checkout-count">
                <span>
                  <ShoppingBag size={16} />
                  {cartCount}{" "}
                  {cartCount === 1 ? "item" : "items"}
                </span>

                <strong>
                  ₹{formatPrice(cartTotal)}
                </strong>
              </div>

              <div className="theme10-checkout-divider" />

              {/* BACKEND CALCULATED AMOUNTS */}
              <div className="theme10-checkout-price-row">
                <span>Subtotal</span>

                <strong>
                  {loading || !priceReady
                    ? "—"
                    : `₹${formatPrice(priceSummary.subtotal)}`}
                </strong>
              </div>

              <div className="theme10-checkout-price-row">
                <span>Tax</span>

                <strong>
                  {loading || !priceReady
                    ? "—"
                    : `₹${formatPrice(priceSummary.tax)}`}
                </strong>
              </div>

              <div className="theme10-checkout-price-row">
                <span>Service Charge</span>

                <strong>
                  {loading || !priceReady
                    ? "—"
                    : `₹${formatPrice(
                        priceSummary.serviceCharge
                      )}`}
                </strong>
              </div>

              <div className="theme10-checkout-divider" />

              <div className="theme10-checkout-total">
                <div>
                  <span>TOTAL</span>
                  <small>
                    Including applicable charges
                  </small>
                </div>

                <strong>
                  {loading || !priceReady
                    ? "—"
                    : `₹${formatPrice(priceSummary.total)}`}
                </strong>
              </div>

              {/* ERROR */}
              {error && (
                <div
                  className="theme10-checkout-error"
                  role="alert"
                >
                  <strong>
                    Unable to calculate total
                  </strong>
                  <p>{error}</p>
                </div>
              )}

              {/* CONTINUE */}
              <button
                type="button"
                className="theme10-checkout-continue"
                onClick={handleContinue}
                disabled={
                  loading ||
                  Boolean(error) ||
                  !priceReady
                }
              >
                {loading ? (
                  <>
                    <span className="theme10-checkout-spinner" />
                    Calculating...
                  </>
                ) : (
                  <>
                    Continue to Order
                    <ArrowRight size={18} />
                  </>
                )}
              </button>

              <button
                type="button"
                className="theme10-checkout-edit"
                onClick={handleBackToCart}
              >
                <ArrowLeft size={15} />
                Edit Your Cart
              </button>

              <div className="theme10-checkout-secure">
                <span>
                  <LockKeyhole size={16} />
                </span>

                <p>
                  Choose your payment method
                  on the next step.
                </p>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* BOTTOM NOTE */}
      <section className="theme10-checkout-footer-note">
        <div className="theme10-checkout-container">
          <span>
            <Leaf size={18} />
          </span>

          <p>
            Every delicious meal starts with fresh
            ingredients and a little extra care.
          </p>
        </div>
      </section>
    </main>
  );
}

export default CheckoutScreen;
